import { createTheme } from '@mui/material/styles';
import { arEG } from '@mui/material/locale';

// Material Design 3 Expressive Theme
export const theme = createTheme(
  {
    direction: 'rtl',
    palette: {
      mode: 'light',
      primary: {
        main: '#1E88E5',
        light: '#6AB7FF',
        dark: '#005CB2',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#7C4DFF',
        light: '#B47CFF',
        dark: '#3F1DCB',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#D32F2F',
        light: '#EF5350',
        dark: '#C62828',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FF9800',
        light: '#FFB74D',
        dark: '#F57C00',
        contrastText: '#000000',
      },
      success: {
        main: '#00C853',
        light: '#69F0AE',
        dark: '#00600F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#1E88E5',
        light: '#6AB7FF',
        dark: '#005CB2',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#1C1B1F',
        secondary: '#49454F',
      },
    },
    typography: {
      fontFamily: '"IBM Plex Sans Arabic", "Roboto", "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontSize: '3.5rem',
        fontWeight: 700,
        lineHeight: 1.167,
        letterSpacing: '-0.01562em',
      },
      h2: {
        fontSize: '2.75rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.00833em',
      },
      h3: {
        fontSize: '2.25rem',
        fontWeight: 600,
        lineHeight: 1.167,
        letterSpacing: '0em',
      },
      h4: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.235,
        letterSpacing: '0.00735em',
      },
      h5: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.334,
        letterSpacing: '0em',
      },
      h6: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.6,
        letterSpacing: '0.0075em',
      },
      subtitle1: {
        fontSize: '1.375rem',
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: '0.00938em',
      },
      subtitle2: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: '0.02857em',
        textTransform: 'none',
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 2.66,
        letterSpacing: '0.08333em',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: 16, // M3 Expressive - larger radius
    },
    spacing: 8, // Base spacing unit (8px)
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 100, // Fully rounded pills
            textTransform: 'none',
            minHeight: 44,
            minWidth: 44,
            fontWeight: 500,
            padding: '8px 24px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            minWidth: 44,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 24, // M3 large
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
          elevation1: {
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          },
          elevation2: {
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
          },
          elevation3: {
            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
    },
  },
  arEG // Arabic locale
);

// Dark theme variant
export const darkTheme = createTheme(
  {
    ...theme,
    palette: {
      mode: 'dark',
      primary: {
        main: '#90CAF9',
        light: '#B3D9FF',
        dark: '#42A5F5',
        contrastText: '#003258',
      },
      secondary: {
        main: '#B39DDB',
        light: '#D1C4E9',
        dark: '#9575CD',
        contrastText: '#311B92',
      },
      error: {
        main: '#EF9A9A',
        light: '#FFCDD2',
        dark: '#E57373',
        contrastText: '#690005',
      },
      warning: {
        main: '#FFB74D',
        light: '#FFD9B4',
        dark: '#FFA726',
        contrastText: '#4E2600',
      },
      success: {
        main: '#69F0AE',
        light: '#B9F6CA',
        dark: '#00E676',
        contrastText: '#003819',
      },
      background: {
        default: '#121212',
        paper: '#1C1B1F',
      },
      text: {
        primary: '#E6E1E5',
        secondary: '#CAC4D0',
      },
    },
  },
  arEG
);
